"use client";

import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Megaphone,
  UploadIcon,
  DownloadCloudIcon,
  TrashIcon,
  Edit,
  Calendar,
} from "lucide-react";
import BookingTable from "./common/Table";
import Button from "./Masters/Button";
import AddDynamicPackageForm from "./AddDynamicPackageForm";
import { useGetTourDetails } from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";

interface TourPackageRow {
  id: number;
  name: string;
  tourType: string;
  specification: string;
  price?: string;
  vendorPrice?: string;
  discountPrice?: string;
  childPrice?: string;
  childDiscount?: string;
  infantPrice?: string;
  infantDiscount?: string;
  packageValidity?: {
    from: string;
    to: string;
  };
  bookingValidity?: {
    from: string;
    to: string;
  };
}

export default function TourPackage() {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [tourData, setTourData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const { mutate: fetchTours, isPending } = useGetTourDetails();

  // Function to fetch tour details
  const loadTourDetails = () => {
    if (user?.company_id) {
      setIsLoading(true);
      fetchTours(
        { company_id: user.company_id, status: 1 },
        {
          onSuccess: (data: any) => {
            console.log("Tour details fetched:", data);
            // Normalize the response data
            let tours = [];
            if (data?.status === "success" && Array.isArray(data?.data)) {
              tours = data.data;
            } else if (Array.isArray(data?.data)) {
              tours = data.data;
            } else if (Array.isArray(data?.responseData?.response?.data)) {
              tours = data.responseData.response.data;
            } else if (Array.isArray(data)) {
              tours = data;
            }
            setTourData(tours);
            setIsLoading(false);
          },
          onError: (err: any) => {
            console.error("Error fetching tour details:", err?.response?.data || err);
            setTourData([]);
            setIsLoading(false);
          },
        }
      );
    } else {
      setIsLoading(false);
    }
  };

  // Fetch tour details on component mount
  useEffect(() => {
    loadTourDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.company_id]);

  // Refresh data when coming back from add form
  useEffect(() => {
    if (!showAddForm && user?.company_id) {
      loadTourDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddForm]);

  // Map API data to table format
  const tableData = tourData.map((item: any, index: number) => ({
    sno: index + 1,
    id: item.id,
    name: item.tours_name || "-",
    tourType: item.tours_type_name || item.tour_type || "-",
    specification: item.package_specification || "-",
    price: item.tours_price ? `₹${item.tours_price}` : "-",
    vendorPrice: item.vendor_price ? `₹${item.vendor_price}` : "-",
    discountPrice: item.discount_price ? `₹${item.discount_price}` : "-",
    childPrice: item.child_price ? `₹${item.child_price}` : "-",
    childDiscount: item.child_discount ? `₹${item.child_discount}` : "-",
    infantPrice: item.infant_price ? `₹${item.infant_price}` : "-",
    infantDiscount: item.infant_discount ? `₹${item.infant_discount}` : "-",
    packageValidity:
      item.package_validity_from && item.package_validity_to
        ? `From: ${item.package_validity_from} To: ${item.package_validity_to}`
        : "-",
    bookingValidity:
      item.booking_validity_from && item.booking_validity_to
        ? `From: ${item.booking_validity_from} To: ${item.booking_validity_to}`
        : "-",
    originalData: item,
  }));

  const columns = [
    {
      key: "selection",
      header: "",
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== row.id));
            }
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    { key: "id", header: "Id", sortable: true },
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (name: string) => (
        <div className="max-w-[150px] truncate" title={name}>
          {name}
        </div>
      ),
    },
    {
      key: "tourType",
      header: "Tour Type",
      sortable: true,
      render: (tourType: string) => (
        <div className="max-w-[150px] truncate" title={tourType}>
          {tourType}
        </div>
      ),
    },
    {
      key: "specification",
      header: "Specification",
      sortable: true,
      render: (specification: string) => (
        <div className="max-w-[200px] truncate" title={specification}>
          {specification}
        </div>
      ),
    },
    {
      key: "price",
      header: "Price",
      sortable: true,
      render: (price: string) => (
        <div className="text-right">{price}</div>
      ),
    },
    {
      key: "vendorPrice",
      header: "Vendor Price",
      sortable: true,
      render: (vendorPrice: string) => (
        <div className="text-right">{vendorPrice}</div>
      ),
    },
    {
      key: "discountPrice",
      header: "Discount Price",
      sortable: true,
      render: (discountPrice: string) => (
        <div className="text-right">{discountPrice}</div>
      ),
    },
    {
      key: "childPrice",
      header: "Child Price",
      sortable: true,
      render: (childPrice: string) => (
        <div className="text-right">{childPrice}</div>
      ),
    },
    {
      key: "childDiscount",
      header: "Child Discount",
      sortable: true,
      render: (childDiscount: string) => (
        <div className="text-right">{childDiscount}</div>
      ),
    },
    {
      key: "infantPrice",
      header: "Infant Price",
      sortable: true,
      render: (infantPrice: string) => (
        <div className="text-right">{infantPrice}</div>
      ),
    },
    {
      key: "infantDiscount",
      header: "Infant Discount",
      sortable: true,
      render: (infantDiscount: string) => (
        <div className="text-right">{infantDiscount}</div>
      ),
    },
    {
      key: "packageValidity",
      header: "Package Validity",
      sortable: true,
      render: (packageValidity: string) => (
        <div className="max-w-[200px] truncate" title={packageValidity}>
          {packageValidity}
        </div>
      ),
    },
    {
      key: "bookingValidity",
      header: "Booking Validity",
      sortable: true,
      render: (bookingValidity: string) => (
        <div className="max-w-[200px] truncate" title={bookingValidity}>
          {bookingValidity}
        </div>
      ),
    },
    {
      key: "action",
      header: "Action",
      sortable: false,
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                // Handle edit validity
                console.log("Edit validity for:", row.id);
              }}
              title="Edit Validity"
            >
              <Calendar size={15} />
            </button>
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                // Handle edit package
                console.log("Edit package:", row.id);
              }}
              title="Edit Package"
            >
              <Edit size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  // Select all checkbox handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handlePublish = () => {
    if (selectedRows.length === 0) return;
    console.log("Publish packages:", selectedRows);
    // Add publish logic here
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) return;
    console.log("Unpublish packages:", selectedRows);
    // Add unpublish logic here
  };

  const handleDelete = () => {
    if (selectedRows.length === 0) return;
    console.log("Delete packages:", selectedRows);
    // Add delete logic here
  };

  if (showAddForm) {
    return <AddDynamicPackageForm onBack={() => setShowAddForm(false)} />;
  }

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      <div className="mt-5">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => setShowAddForm(true)}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add Dynamic Package
          </Button>

          <Button
            size="sm"
            onClick={() => {
              console.log("Add Quick Package");
            }}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add Quick Package
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              console.log("Search / Filter");
            }}
            icon={<Search className="w-4 h-4" />}
            iconPosition="left"
          >
            Search / Filter
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              console.log("Deal's");
            }}
            icon={<Megaphone className="w-4 h-4" />}
            iconPosition="left"
          >
            Deal&apos;s
          </Button>

          <Button
            size="sm"
            variant="success"
            onClick={handlePublish}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Publish
          </Button>

          <Button
            size="sm"
            variant="warning"
            onClick={handleUnpublish}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Unpublish
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Delete
          </Button>
        </div>

        <BookingTable
          data={tableData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={true}
          sortable={true}
          selectable={true}
          onSelectionChange={setSelectedRows}
          loading={isLoading || isPending}
          renderHeader={() => (
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === tableData.length &&
                      tableData.length > 0
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Id
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tour Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Specification
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Child Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Child Discount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Infant Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Infant Discount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Package Validity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking Validity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
          )}
        />
      </div>
    </div>
  );
}
